import React from "react";
import { IoIosArrowDown, IoMdSettings } from "react-icons/io";
import { FiEdit2, FiEye, FiTrash2 } from "react-icons/fi";
import { ReactSVG } from "react-svg";
import { toast } from "react-toastify";

const PromptDropdown = ({
  isMobile,
  isOpen,
  setIsOpen,
  selected,
  promptOptions,
  setSelected,
  onMakeDefault,
  defaultPromptId,
  onEditClick,
  onViewClick,
  onDeleteClick,
  isPro = false,
}) => {
  return (
    <div className={`relative ${isMobile ? "flex lg:hidden" : "lg:flex hidden"} flex-col`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{ "--bgs": selected?.bgColor }}
        className="flex justify-between items-center p-3 my-2 md:text-base text-sm bg-[#F1FAFF] rounded-lg font-inter w-full text-[#363636]"
      >
        <span className="flex items-center gap-2">
          <span className="p-2 rounded-md transition bg-[var(--bgs)]">
            <ReactSVG
              src={selected.icon}
              beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
            />
          </span>
          {selected.label}
        </span>
        <div className="flex items-center gap-2 text-[#363636]">
          {selected?.isSystemGenerated === false && (
            <>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPro) {
                    toast.info("Upgrade to pro to create and manage custom prompts");
                    return;
                  }
                  onEditClick?.(selected);
                }}
                className="p-1 hover:bg-[#E1F3FF] rounded transition text-[#5C6370] hover:text-[#3AB1F6] cursor-pointer"
                title="Edit Prompt"
              >
                <FiEdit2 className="w-4 h-4" />
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPro) {
                    toast.info("Upgrade to pro to create and manage custom prompts");
                    return;
                  }
                  onDeleteClick?.(selected);
                }}
                className="p-1 hover:bg-[#E1F3FF] rounded transition text-[#5C6370] hover:text-[#E11D48] cursor-pointer"
                title="Delete Prompt"
              >
                <FiTrash2 className="w-4 h-4" />
              </span>
            </>
          )}
          <span
            onClick={(e) => {
              e.stopPropagation();
              onViewClick?.(selected);
            }}
            className="p-1 hover:bg-[#E1F3FF] rounded transition text-[#5C6370] hover:text-[#3AB1F6] cursor-pointer"
            title="Preview Prompt"
          >
            <FiEye className="w-4 h-4" />
          </span>
          <IoIosArrowDown
            className={`transition-transform duration-300 ${isOpen ? "md:rotate-180 rotate-0" : "md:rotate-0 rotate-180"
              }`}
          />
        </div>
      </button>

      <ul
        className={`absolute w-full rounded-lg max-h-36 overflow-y-auto bg-[#F1FAFF] transform transition-all duration-500 origin-top top-16 z-50 ${!isMobile ? "shadow-md custom-scrollbar" : ""
          } ${isOpen ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0 pointer-events-none"}`}
      >
        {promptOptions.map((opt, idx) => {
          const isCustom = opt.isSystemGenerated === false;
          const isDimmed = !isPro && isCustom;

          return (
            <li
              key={opt.id || idx}
              style={{
                "--bg": opt?.bgColor,
                opacity: isDimmed ? 0.75 : 1,
              }}
              className="group flex items-center justify-between gap-2 p-3 text-base font-inter transition cursor-pointer hover:bg-[#3AB1F6] hover:text-white"
            >
              <button
                type="button"
                onClick={() => {
                  if (isDimmed) {
                    toast.info("Upgrade to pro to create and manage custom prompts");
                    return;
                  }
                  setSelected(opt);
                  setIsOpen(false);
                }}
                className="flex min-w-0 flex-1 items-center gap-2 text-left cursor-pointer"
              >
                <span className="p-2 rounded-md transition bg-[var(--bg)] group-hover:bg-white">
                  <ReactSVG
                    src={opt.icon}
                    beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                  />
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isDimmed) {
                      toast.info("Upgrade to pro to create and manage custom prompts");
                      return;
                    }
                    onViewClick?.(opt);
                  }}
                  className="p-1 hover:bg-[#E1F3FF] group-hover:hover:bg-[#2fa0e0] rounded transition text-[#5C6370] group-hover:text-white hover:text-white cursor-pointer"
                  title="Preview Prompt"
                >
                  <FiEye className="w-3.5 h-3.5" />
                </button>

                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isDimmed) {
                      toast.info("Upgrade to pro to create and manage custom prompts");
                      return;
                    }
                    onMakeDefault?.(opt);
                  }}
                  className="shrink-0 rounded-md bg-white px-2 py-1 text-[11px] font-medium text-[#3AB1F6] transition hover:bg-[#EAF7FF] cursor-pointer"
                >
                  {defaultPromptId === opt.id ? "Default" : "Make default"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const SettingsPanel = ({
  dropdownRef,
  isOpen,
  setIsOpen,
  selected,
  setSelected,
  promptOptions,
  onMakeDefault,
  defaultPromptId,
  hastext,
  applyLoading = false,
  onApplySettings,
  onAddClick,
  onEditClick,
  onViewClick,
  onDeleteClick,
  isPro = false,
}) => {
  return (
    <div className="rounded-2xl border border-[#D6ECFB] bg-white p-4">
      <div className="flex justify-between items-center w-full mb-3">
        <div className="shrink-0 flex items-center gap-3 md:text-[1.75rem] text-xl font-semibold font-inter">
          <span className="bg-gradient-to-b from-[#3AB1F6] to-[#407BFF] text-white p-1.5 rounded-lg">
            <IoMdSettings className="md:size-5 size-4" />
          </span>
          Prompts
        </div>
        <button
          type="button"
          onClick={() => {
            if (!isPro) {
              toast.info("Upgrade to pro to create and manage custom prompts");
              return;
            }
            onAddClick?.();
          }}
          className={`rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:opacity-90 text-white px-4 py-1.5 text-xs font-semibold font-inter shadow-sm hover:shadow transition-all flex items-center gap-1 shrink-0 cursor-pointer ${
            !isPro ? "opacity-75" : ""
          }`}
        >
          <span>+ Add Prompt</span>
        </button>
      </div>

      <div className="w-full mt-3" ref={dropdownRef}>
        <PromptDropdown
          isMobile={false}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          selected={selected}
          promptOptions={promptOptions}
          setSelected={setSelected}
          onMakeDefault={onMakeDefault}
          defaultPromptId={defaultPromptId}
          onEditClick={onEditClick}
          onViewClick={onViewClick}
          onDeleteClick={onDeleteClick}
          isPro={isPro}
        />

        <PromptDropdown
          isMobile
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          selected={selected}
          promptOptions={promptOptions}
          setSelected={setSelected}
          onMakeDefault={onMakeDefault}
          defaultPromptId={defaultPromptId}
          onEditClick={onEditClick}
          onViewClick={onViewClick}
          onDeleteClick={onDeleteClick}
          isPro={isPro}
        />
      </div>

      {hastext && (
        <button
          onClick={onApplySettings}
          disabled={applyLoading}
          className="w-full mt-4 shrink-0 bg-[#3AB1F6] p-3 rounded-xl text-xl font-inter font-medium text-white"
        >
          {applyLoading ? "Applying..." : "Apply Prompt"}
        </button>
      )}
    </div>
  );
};

export default SettingsPanel;
